import React from 'react';
import { EntryActionTypes, Status, StatusTypes } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { getCustomUserName } from '@brighterion/utils/users';
import { getElapsedTime } from '@brighterion/utils/dates-helpers';
import { IMPORT_EXPORT_PROCESS_TYPE, IMPORT_EXPORT_STATUS, SIDE_PANEL_CONTENT_TYPE } from 'Common/enums';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetImportExportStatus from 'Hooks/query-hooks/side-panel/useGetImportExportStatus';
import { useListFilesDownloadStore } from 'Hooks/zustand/useListFilesDownloadStore';

type FileDownloadStatus = {
    status: {
        type: keyof typeof StatusTypes;
        label: string;
        previousStatus?: Status;
    };
    isDownloadFilesAllowed: boolean;
    isDownloading: boolean;
    buttonText?: string;
};

const useGetSidePanelContentData = () => {
    const { t } = useTranslation();
    const importExportStatus = useGetImportExportStatus();
    const importExportData = importExportStatus?.data;
    const { users } = useGetReduxUsers();
    const { filesDownloading } = useListFilesDownloadStore();
    const now = new Date();

    const getFileDownloadDetails = (data: {
        id: string;
        isImport: boolean;
        status: string;
        fileName: string;
        originalFileName: string | undefined;
    }): FileDownloadStatus => {
        const { id, isImport, fileName, originalFileName, status } = data;
        const fileDownloadItem = filesDownloading.find((file) => file.id === id);
        let isDownloadFilesAllowed = false;
        const dataItemStatus = { type: data.status as keyof typeof StatusTypes, label: t(data.status) };
        if (isImport) {
            const isAllowedByStatus =
                status === IMPORT_EXPORT_STATUS.FINISHED ||
                data.status === IMPORT_EXPORT_STATUS.CANCELLED ||
                data.status === IMPORT_EXPORT_STATUS.FAILED;
            // if fileName is updated and not matching with originalFileName, means new file was generated
            if (originalFileName && fileName !== originalFileName && isAllowedByStatus) {
                isDownloadFilesAllowed = true;
            }
        } else if (status === IMPORT_EXPORT_STATUS.FINISHED) {
            isDownloadFilesAllowed = true;
        }
        if (isDownloadFilesAllowed && fileDownloadItem) {
            if (fileDownloadItem?.partCompleted && fileDownloadItem?.totalParts) {
                const percentageCompleted = Math.round(
                    (fileDownloadItem?.partCompleted / fileDownloadItem?.totalParts) * 100
                );
                return {
                    isDownloadFilesAllowed,
                    isDownloading: true,
                    buttonText: t('Cancel'),
                    status: {
                        type: 'DOWNLOADING',
                        label: t('Downloading Files', { percentageCompleted }),
                        previousStatus: dataItemStatus
                    }
                };
            }
            return {
                isDownloadFilesAllowed,
                isDownloading: true,
                buttonText: t('Cancel'),
                status: {
                    type: 'DOWNLOADING',
                    label: t('Downloading Files', { percentageCompleted: 0 }),
                    previousStatus: dataItemStatus
                }
            };
        }
        if (isDownloadFilesAllowed) {
            const details = {
                isDownloadFilesAllowed,
                isDownloading: false,
                buttonText: t('Download Files'),
                status: dataItemStatus
            };
            return details;
        }
        return {
            isDownloadFilesAllowed: false,
            isDownloading: false,
            buttonText: undefined,
            status: dataItemStatus
        };
    };

    const listDataTransformer = (dataItem: ImportExportAPIResponse, isQueued?: boolean) => {
        const isImport = dataItem.processType === IMPORT_EXPORT_PROCESS_TYPE.IMPORT;

        const fileDownloadDetails = getFileDownloadDetails({
            id: dataItem.id as string,
            isImport,
            fileName: dataItem.fileName,
            originalFileName: dataItem?.progressDetails?.importFilename,
            status: dataItem.status
        });

        let result: SidePanelAccordionItemContentCardData = {
            id: dataItem.id as string,
            entryName: dataItem.listName,
            entryActionType: dataItem.processType as IMPORT_EXPORT_PROCESS_TYPE,
            fileName: dataItem.fileName,
            actionDetails: {
                icon: isImport ? 'arrow-up-outline' : 'rectangle-stack-outline',
                description: isImport ? t('Import entries from CSV') : t('Export all entries to CSV')
            },
            status: fileDownloadDetails.status,
            creationDetail: t('Started By:', { startedBy: getCustomUserName(dataItem.createdBy, users) }),
            timeDetail: '',
            progressDetails: dataItem.progressDetails
        };
        // queued section
        if (isQueued) {
            const createdAt = new Date(dataItem.createdAt);
            const timeDetail = getElapsedTime(now, createdAt);

            const isQueuedOrInProgress =
                dataItem.status === IMPORT_EXPORT_STATUS.QUEUED || dataItem.status === IMPORT_EXPORT_STATUS.IN_PROGRESS;
            const actionText = isQueuedOrInProgress ? t('Cancel') : undefined;
            const cancelActionDetails = isQueuedOrInProgress
                ? {
                      id: Number(dataItem.id),
                      type: isImport ? EntryActionTypes.IMPORT : EntryActionTypes.EXPORT,
                      status: dataItem.status,
                      listName: dataItem.listName
                  }
                : undefined;

            result = {
                ...result,
                timeDetail: t('Started At', { startedAt: timeDetail.elapsedTime, format: timeDetail.format }),
                actionText,
                cancelActionDetails
            };
        }
        // completed Section
        else {
            const expiryDate = new Date(dataItem.expiryDate ?? 0);
            const timeDetail = getElapsedTime(expiryDate, now);

            const commonResult = {
                ...result,
                timeDetail: t('Expires In', { expAt: timeDetail.elapsedTime, format: timeDetail.format }),
                actionText: fileDownloadDetails?.buttonText,
                downloadActionDetails: {
                    isDownloadAllowed: fileDownloadDetails.isDownloadFilesAllowed && !!fileDownloadDetails?.buttonText,
                    downloadFileName: dataItem.fileName,
                    isCancelDownload: fileDownloadDetails.isDownloading
                }
            };
            result = { ...commonResult };
        }

        return result;
    };

    const getData = React.useCallback(
        (type: SIDE_PANEL_CONTENT_TYPE) => {
            // filter import/export queue entries for correct expiry date of entries and remove negative entries
            const contentData =
                importExportData?.[type]?.filter((item) => {
                    const expiryDate = new Date(item.expiryDate ?? 0);
                    const timeDetail = getElapsedTime(expiryDate, now);
                    if (timeDetail.elapsedTime < 0) {
                        return false;
                    }
                    return true;
                }) ?? [];
            return contentData.map((item) => listDataTransformer(item, type === SIDE_PANEL_CONTENT_TYPE.QUEUE));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [importExportData, filesDownloading, importExportStatus.isRefetching]
    );
    return getData;
};

export default useGetSidePanelContentData;
